import type {
  PreviewProfileTypeBlockResponse,
  PreviewTypeRemovalResponse,
  ProfileTypeStats,
  RemoveTypeResponse,
  RunProfileTypeBlockResponse,
  TypeRemoval,
  UndoTypeRemovalResponse,
} from '@shared/schemas';
import { all, call, delay, getContext, put, select, takeEvery, takeLatest } from 'redux-saga/effects';

import { classifyInfraError } from '@/utils/infra-error';

import { draftToPayload, draftToPreviewPayload, isDraftPreviewable } from '../domain/block-draft';
import type { ProfileTypeBlock } from '../domain/profile-type.model';
import type { ProfileTypesGateway } from '../infrastructure/profile-types.gateway';
import {
  automaticToggleRequested,
  blocksRequested,
  clearRequested,
  draftPreviewRequested,
  removalPreviewRequested,
  removalRequested,
  removalsRequested,
  runPreviewRequested,
  runRequested,
  saveRequested,
  typePriorityRequested,
  typePrioritySaveRequested,
  undoRemovalRequested,
} from './profile-types.actions';
import { selectBlocks, selectTypePriority } from './profile-types.selectors';
import { profileTypesActions as actions } from './profile-types.slice';
import type { TypePriorityState } from './profile-types.state';

/** How long the editor waits after the last edit before asking for a fresh count. */
export const DRAFT_PREVIEW_DELAY_MS = 600;

/** The backend's own message when it has one (a 400 names the faulty condition). */
function toMessage(e: unknown, fallback: string): string {
  return classifyInfraError(e, fallback).message;
}

function* gatewayOf() {
  const gateway: ProfileTypesGateway = yield getContext('profileTypesGateway');
  return gateway;
}

/**
 * WORKER: the overview's figures. Silent on failure: the page keeps working without
 * its numbers, and an error toast for a side panel would read as "the page failed".
 */
function* statsWorker() {
  const gateway: ProfileTypesGateway = yield* gatewayOf();
  try {
    const stats: ProfileTypeStats = yield call(gateway.loadStats);
    yield put(actions.statsSet(stats));
  } catch {
    // Figures stay as they were (or absent).
  }
}

/**
 * Refetch the list and store it silently (no spinner), and the figures with it: a
 * save, a run or a new priority all move "profiles found".
 */
function* reload() {
  const gateway: ProfileTypesGateway = yield* gatewayOf();
  const blocks: ProfileTypeBlock[] = yield call(gateway.loadBlocks);
  yield put(actions.blocksSet(blocks));
  yield* statsWorker();
}

/** "N profiles" with the plural right. */
const profiles = (n: number) => `${n.toLocaleString()} profile${n === 1 ? '' : 's'}`;

/**
 * WORKER: "Recent removals" (#652). Silent on failure, like the figures: the page
 * works without its history.
 */
function* removalsWorker() {
  const gateway: ProfileTypesGateway = yield* gatewayOf();
  try {
    const removals: TypeRemoval[] = yield call(gateway.loadRemovals);
    yield put(actions.removalsSet(removals));
  } catch {
    // History stays as it was (or empty).
  }
}

/** WORKER: initial load (drives the first-mount spinner). */
function* loadWorker() {
  const gateway: ProfileTypesGateway = yield* gatewayOf();
  yield put(actions.loadStarted());
  try {
    const blocks: ProfileTypeBlock[] = yield call(gateway.loadBlocks);
    yield put(actions.loadSucceeded(blocks));
  } catch {
    yield put(actions.loadFailed());
    return;
  }
  yield* statsWorker();
  yield* removalsWorker();
}

/**
 * WORKER: save the editor's block, then refresh.
 *
 * The refresh is a refetch rather than splicing the response in: a new type also
 * lands in the priority order, and the list should show the keywords as they were
 * normalised, not as they were typed. For a new type the Priority tab is reloaded too.
 *
 * With `thenApply`, the apply dialog opens on the type once the save has landed; so
 * what gets applied is the block as stored, never a draft that failed to save.
 */
function* saveWorker(action: ReturnType<typeof saveRequested>) {
  const { draft, thenApply } = action.payload;
  const gateway: ProfileTypesGateway = yield* gatewayOf();
  const name = draft.name.trim();

  yield put(actions.saveStarted());
  try {
    const saved: ProfileTypeBlock = yield call(gateway.saveBlock, name, draftToPayload(draft));
    yield put(
      actions.noticeSet({
        success: draft.isNew ? `Profile type "${saved.name}" created.` : `Profile type "${saved.name}" saved.`,
        error: null,
      }),
    );
    yield* reload();
    if (draft.isNew) yield* typePriorityWorker();
    if (thenApply) yield put(actions.applyTargetSet(saved.name));
  } catch (e) {
    yield put(actions.noticeSet({ success: null, error: toMessage(e, 'Failed to save the profile type') }));
  } finally {
    yield put(actions.saveStopped());
  }
}

/**
 * WORKER: flip a type's Automatic switch from the overview.
 *
 * There is no partial update: the stored block is resent with the switch changed, so
 * the switch can never leave half a block behind. `takeEvery`, not `takeLatest`: each
 * row's switch is its own operation, and `takeLatest` would cancel the first of two
 * quick toggles on different rows, leaving a switch showing a state never written.
 */
function* toggleAutomaticWorker(action: ReturnType<typeof automaticToggleRequested>) {
  const { name, autoClassify } = action.payload;
  const gateway: ProfileTypesGateway = yield* gatewayOf();
  const blocks: ProfileTypeBlock[] = yield select(selectBlocks);
  const block = blocks.find((b) => b.name === name);
  if (!block || block.category === null) return;

  yield put(actions.toggleStarted(name));
  try {
    const saved: ProfileTypeBlock = yield call(gateway.saveBlock, name, {
      category: block.category,
      auto_classify: autoClassify,
      conditions: block.conditions,
    });
    yield put(actions.blockReplaced(saved));
  } catch (e) {
    yield put(
      actions.noticeSet({
        success: null,
        error: toMessage(e, autoClassify ? 'Failed to turn automatic tagging on' : 'Failed to turn automatic tagging off'),
      }),
    );
    // The switch shows the state we failed to write; only the list can put it back.
    yield* reload();
  } finally {
    yield put(actions.toggleStopped(name));
  }
}

/** WORKER: remove a type's block, then refresh. */
function* clearWorker(action: ReturnType<typeof clearRequested>) {
  const { name } = action.payload;
  const gateway: ProfileTypesGateway = yield* gatewayOf();

  yield put(actions.clearStarted(name));
  try {
    yield call(gateway.clearBlock, name);
    yield put(
      actions.noticeSet({
        // Says what removing does NOT do: an admin removing a mistyped block will
        // otherwise assume the tags went with it.
        success: `Conditions of "${name}" removed. Profiles already tagged keep the type.`,
        error: null,
      }),
    );
    yield* reload();
  } catch (e) {
    yield put(actions.noticeSet({ success: null, error: toMessage(e, 'Failed to remove the conditions') }));
  } finally {
    yield put(actions.clearStopped());
  }
}

/** WORKER: count what applying a stored block would tag, for the dialog's gate. */
function* runPreviewWorker(action: ReturnType<typeof runPreviewRequested>) {
  const gateway: ProfileTypesGateway = yield* gatewayOf();

  yield put(actions.runPreviewStarted());
  try {
    const preview: PreviewProfileTypeBlockResponse = yield call(gateway.previewRun, action.payload.name);
    yield put(actions.runPreviewSucceeded(preview));
  } catch {
    // No snackbar: the dialog says so in place of the count. An error toast on opening
    // a dialog reads as "the run failed", which has not happened.
    yield put(actions.runPreviewFailed());
  }
}

/**
 * WORKER: the editor's live count.
 *
 * `takeLatest` plus a delay is the debounce: every edit restarts this worker, the
 * previous one is cancelled while it waits, and only a pause in editing reaches the
 * server. The panel shows "counting" at once, so the old figure never sits beside
 * keywords it was not asked about.
 *
 * A draft that cannot match anything yet is not sent (the server would answer 400)
 * and the panel goes back to idle, where it says what is missing.
 *
 * No snackbar on failure: only a read was attempted; the panel says it in place.
 */
function* draftPreviewWorker(action: ReturnType<typeof draftPreviewRequested>) {
  const { draft } = action.payload;
  if (!isDraftPreviewable(draft)) {
    yield put(actions.draftPreviewCleared());
    return;
  }
  const gateway: ProfileTypesGateway = yield* gatewayOf();

  yield put(actions.draftPreviewStarted());
  yield delay(DRAFT_PREVIEW_DELAY_MS);
  try {
    const preview: PreviewProfileTypeBlockResponse = yield call(gateway.previewDraft, draftToPreviewPayload(draft));
    yield put(actions.draftPreviewSucceeded(preview));
  } catch {
    yield put(actions.draftPreviewFailed());
  }
}

/**
 * WORKER: apply a stored block over the whole base, then refresh. The refresh keeps
 * the overview in step with what just happened, so nobody applies it twice thinking
 * the first did nothing.
 */
function* runWorker(action: ReturnType<typeof runRequested>) {
  const { name } = action.payload;
  const gateway: ProfileTypesGateway = yield* gatewayOf();

  yield put(actions.runStarted(name));
  try {
    const result: RunProfileTypeBlockResponse = yield call(gateway.runBlock, name);
    yield put(
      actions.noticeSet({
        success:
          result.tagged_count === 0
            ? `No profile to tag; every match already carries "${result.tag}".`
            : `${result.tagged_count.toLocaleString()} profile${result.tagged_count > 1 ? 's' : ''} tagged "${result.tag}".`,
        error: null,
      }),
    );
    yield* reload();
  } catch (e) {
    // A 400 here carries the reason (a block with nothing to match on).
    yield put(actions.noticeSet({ success: null, error: toMessage(e, 'Failed to apply the profile type') }));
  } finally {
    yield put(actions.runStopped());
    yield put(actions.runPreviewCleared());
    yield put(actions.applyTargetSet(null));
  }
}

/**
 * WORKER: open the bulk removal dialog and measure both options (#652). No snackbar
 * on failure: the dialog says it in place of the counts.
 */
function* removalPreviewWorker(action: ReturnType<typeof removalPreviewRequested>) {
  const { name } = action.payload;
  const gateway: ProfileTypesGateway = yield* gatewayOf();

  yield put(actions.removalOpened(name));
  try {
    const preview: PreviewTypeRemovalResponse = yield call(gateway.previewRemoval, name);
    yield put(actions.removalPreviewSucceeded(preview));
  } catch {
    yield put(actions.removalPreviewFailed());
  }
}

/**
 * WORKER: take the type off profiles in bulk, then refresh the list, the figures and
 * the history. The success carries the removal's id so the snackbar can offer Undo.
 */
function* removalWorker(action: ReturnType<typeof removalRequested>) {
  const { name, scope } = action.payload;
  const gateway: ProfileTypesGateway = yield* gatewayOf();

  yield put(actions.removalStarted());
  try {
    const result: RemoveTypeResponse = yield call(gateway.removeType, name, scope);
    // The page closes the dialog on the success notice. The removal state is left as
    // it is so the dialog fades out on its content, not on an empty shell; the next
    // opening resets it.
    yield put(
      actions.noticeSet({
        success:
          result.removed_count === 0
            ? `No profile to change; nothing selected carries "${result.tag}" any more.`
            : `"${result.tag}" removed from ${profiles(result.removed_count)}.`,
        error: null,
        undoRemovalId: result.removal_id,
      }),
    );
    yield* reload();
    yield* removalsWorker();
  } catch (e) {
    yield put(actions.noticeSet({ success: null, error: toMessage(e, 'Failed to remove the type from profiles') }));
  } finally {
    yield put(actions.removalStopped());
  }
}

/** WORKER: undo a bulk removal, then refresh what it moved. */
function* undoRemovalWorker(action: ReturnType<typeof undoRemovalRequested>) {
  const { id } = action.payload;
  const gateway: ProfileTypesGateway = yield* gatewayOf();

  yield put(actions.undoStarted(id));
  try {
    const result: UndoTypeRemovalResponse = yield call(gateway.undoRemoval, id);
    yield put(
      actions.noticeSet({
        success:
          result.restored_count === 0
            ? `Removal undone. Every profile already carried "${result.tag}" again.`
            : `"${result.tag}" put back on ${profiles(result.restored_count)}.`,
        error: null,
      }),
    );
    yield* reload();
    yield* removalsWorker();
  } catch (e) {
    // A 409 says it was already undone; the history is stale, so refresh it too.
    yield put(actions.noticeSet({ success: null, error: toMessage(e, 'Failed to undo the removal') }));
    yield* removalsWorker();
  } finally {
    yield put(actions.undoStopped());
  }
}

/**
 * WORKER: load the type priority order (#554). A failure lands a `failed` status and
 * stays silent: the tab shows its own empty state.
 */
function* typePriorityWorker() {
  const gateway: ProfileTypesGateway = yield* gatewayOf();
  yield put(actions.typePriorityLoadStarted());
  try {
    const names: string[] = yield call(gateway.loadTypeOrder);
    yield put(actions.typePriorityLoaded(names));
  } catch {
    yield put(actions.typePriorityLoadFailed());
  }
}

/**
 * WORKER: persist the order and say how many profiles it moved. The count is the
 * point of the message: reordering rewrites a column on every profile of the base.
 */
function* typePrioritySaveWorker() {
  const gateway: ProfileTypesGateway = yield* gatewayOf();
  const priority: TypePriorityState = yield select(selectTypePriority);
  yield put(actions.typePrioritySaveStarted());
  try {
    const recomputed: number = yield call(gateway.saveTypeOrder, priority.names);
    yield put(actions.typePrioritySaved());
    yield put(
      actions.noticeSet({
        success:
          recomputed === 0
            ? 'Priority saved. No profile changed type.'
            : `Priority saved. ${recomputed.toLocaleString()} profile${recomputed > 1 ? 's' : ''} changed type.`,
        error: null,
      }),
    );
    yield* reload();
  } catch (e) {
    yield put(actions.typePrioritySaveFailed());
    yield put(actions.noticeSet({ success: null, error: toMessage(e, 'Failed to save the type priority') }));
  }
}

/** WATCHERS. */
export function* profileTypesSaga() {
  yield all([
    takeLatest(blocksRequested.type, loadWorker),
    takeLatest(saveRequested.type, saveWorker),
    takeEvery(automaticToggleRequested.type, toggleAutomaticWorker),
    takeLatest(clearRequested.type, clearWorker),
    takeLatest(runPreviewRequested.type, runPreviewWorker),
    takeLatest(draftPreviewRequested.type, draftPreviewWorker),
    takeLatest(runRequested.type, runWorker),
    takeLatest(typePriorityRequested.type, typePriorityWorker),
    takeLatest(typePrioritySaveRequested.type, typePrioritySaveWorker),
    takeLatest(removalPreviewRequested.type, removalPreviewWorker),
    takeLatest(removalRequested.type, removalWorker),
    takeLatest(removalsRequested.type, removalsWorker),
    takeLatest(undoRemovalRequested.type, undoRemovalWorker),
  ]);
}
